import React, { FC, useEffect, useMemo, useState } from 'react';
import { Space, Typography } from 'antd';
// import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import QRCode from 'react-qr-code';

import { crc16, emvEncode } from 'utils/fpsHkQrcode';
import { PAYMENT_CONSTANTS } from 'pages/shortfall/settle/utils/index';
import { SettleItem } from 'pages/shortfall/settle/types';
import { FPSPaymentLogParams, useFPSPaymentLog } from 'all-in-one-shared-api';
import { useKeycloak } from 'all-in-one-react-keycloak';

const { Text } = Typography;

const StyledDiv = styled.div`
  margin: 0.5em 0;
`;

type FPSPanelContentProps = {
  amount: string;
  bankCode: string;
  fpsId: string;
  mobile: string;
  email: string;
  mcc: string;
  currency: string;
  reference: string;
  merchantName: string;
  claims: SettleItem[];
};

export const FPSPanelContent: FC<Partial<FPSPanelContentProps>> = ({
  bankCode,
  fpsId,
  mobile,
  email,
  mcc = PAYMENT_CONSTANTS.FPS_MCC,
  currency = PAYMENT_CONSTANTS.FPS_CURRENCY,
  amount,
  // ITREQ-4064, adjust by SS, use 'GENBRAVO FPS' as reference default value
  reference = 'GENBRAVO FPS',
  merchantName,
  claims,
}) => {
  const { keycloak } = useKeycloak();
  const [qrcodeVal, setQrcodeVal] = useState('');
  const claimIdsStr = claims?.map((c) => `${c.claimId}${c.sequenceNo ? '-' + c.sequenceNo : ''}`).join(',');

  const fPSPaymentLogPayload: FPSPaymentLogParams = {
    jsonData: JSON.stringify({ content: { fpsQRCode: qrcodeVal, claims: claimIdsStr } }),
    media: 'WEB',
    sourceOfRequest: '/claims/shortfall/settle',
  };

  const { shouldFetch } = useFPSPaymentLog({ key: fPSPaymentLogPayload }, keycloak?.token || '', fPSPaymentLogPayload, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  });

  const qrCode = useMemo(() => {
    let qrcode = <Text>Invalid or missing input data.</Text>;
    let account = null;
    if (fpsId) {
      account = '02';
    }
    if (mobile) {
      account = '03';
    }
    if (email) {
      account = '04';
    }

    if (!amount) {
      return qrcode;
    }

    if (account) {
      const message = emvEncode({
        account: account,
        bank_code: bankCode || '',
        fps_id: fpsId,
        mobile: mobile,
        email: email,
        mcc: mcc,
        currency: currency,
        amount: amount,
        reference: reference,
        merchantName: merchantName,
      });

      if (message) {
        const crc = crc16(message).toString(16).padStart(4, '0').toUpperCase();
        setQrcodeVal(message + crc);
        qrcode = <QRCode value={message + crc} size={200} />;
      }
    }

    return qrcode;
  }, [amount, reference, merchantName, mcc, currency, bankCode, email, fpsId, mobile]);

  useEffect(() => {
    if (qrcodeVal) shouldFetch(true);
  }, [shouldFetch, qrcodeVal]);

  // const { t } = useTranslation();

  return (
    <Space direction="vertical" style={{ padding: '0 16px' }}>
      {/* <Text strong>{t('claims.shortfall.payment.scanTheQRBelow')}</Text> */}
      <StyledDiv>{qrCode}</StyledDiv>
      {/* <SecondaryInfoText content={t('claims.shortfall.payment.understandTimeToProcess')} /> */}
    </Space>
  );
};
